import React, { useState } from 'react'
import * as yup from 'yup'
import { Button } from 'primereact/button'
import { confirmDialog } from 'primereact/confirmdialog'

import { notifyError, notifySuccess } from '../features/notificationsManager/Notify'
import {
    LoadBalancerGroup,
    useLoadBalancerGroupUpdateMutation,
    useLoadBalancerGroupDisableGroupMutation,
    useLoadBalancerGroupEnableGroupMutation,
} from '../redux/api/web-api'
import { nameRegex, nameValidation } from '../features/loadbalancer-group/load-balancer-group-form-validation'
import { isAdminDomain } from '../../lib/currentDomain'
import TrashIcon from '../../lib/icons/trash-icon/trash-icon'
import useText from '../hooks/useText'
import { MAX_NAME_LENGTH } from '../constants/InputsLength'

import ComponentTypeLabel from './ComponentTypeLabel'
import DropdownMenu, { DropdownMenuItem } from './DropdownMenu'

interface IComponentProps {
    loadBalancerGroup: LoadBalancerGroup
    editable?: boolean
    hasChargers: boolean
    removeLBGroup: (groupId: number) => void
}

const LoadBalancerTypeLabel = ({
    loadBalancerGroup,
    editable,
    hasChargers,
    removeLBGroup,
}: IComponentProps): React.ReactElement => {
    const text = useText()
    const [edit, setEdit] = useState(false)
    const schema = yup.object().shape({
        name: nameValidation.matches(nameRegex, text('msgWhiteSpacesValidation')).max(MAX_NAME_LENGTH),
    })

    const [disableLoadbalancer, { isLoading: deactivateLBGroupLoading }] = useLoadBalancerGroupDisableGroupMutation()
    const [enableLoadbalancer, { isLoading: activateLBGroupLoading }] = useLoadBalancerGroupEnableGroupMutation()

    const activateLbGroup = async () => {
        if (!loadBalancerGroup?.id) return

        try {
            await enableLoadbalancer({ id: loadBalancerGroup?.id }).unwrap()
            notifySuccess(text('msgLoadbalancerGroupActivated', { loadbalancerGroup: loadBalancerGroup?.name }))
        } catch (error: unknown) {
            notifyError(text('lblLoadbalancerEnableFail'))
        }
    }

    const deactivateLbGroup = async () => {
        if (!loadBalancerGroup?.id) return

        try {
            await disableLoadbalancer({ id: loadBalancerGroup?.id }).unwrap()
            notifySuccess(text('msgLoadbalancerGroupDeactivated', { loadbalancerGroup: loadBalancerGroup?.name }))
        } catch {
            notifyError(text('lblLoadbalancerDisableFail'))
        }
    }

    const [updateLoadBalancerGroup] = useLoadBalancerGroupUpdateMutation()
    const saveData = async (formData: LoadBalancerGroup) => {
        const tempLoadBalancerGroup = {
            ...loadBalancerGroup,
            ...formData,
            name: formData?.name?.trim(),
        }
        try {
            await updateLoadBalancerGroup({
                loadBalancerGroup: tempLoadBalancerGroup,
            }).unwrap()
            notifySuccess(text('msgLoadbalancerGroupUpdate'))
            setEdit(false)
        } catch (error) {
            notifyError(text('msgLoadbalancerGroupUpdateError'))
        }
    }
    return (
        <>
            {loadBalancerGroup && (
                <ComponentTypeLabel
                    name={loadBalancerGroup?.name}
                    schema={schema}
                    saveData={saveData}
                    edit={edit}
                    setEdit={setEdit}
                    editable={editable}
                />
            )}

            <div className="flex justify-content-between gap-5">
                {!isAdminDomain && loadBalancerGroup && (
                    <>
                        {loadBalancerGroup?.active ? (
                            <Button
                                loading={deactivateLBGroupLoading}
                                label={text('btnDeactivate')}
                                onClick={() => {
                                    confirmDialog({
                                        message: text('msgDisableLoadBalancingGroup'),
                                        header: text('btnDeActivateLB'),
                                        accept: () => {
                                            deactivateLbGroup()
                                        },
                                    })
                                }}
                                data-testid="deactivate-LB-button"
                            />
                        ) : (
                            <Button
                                loading={activateLBGroupLoading}
                                label={text('btnActivateLB')}
                                onClick={activateLbGroup}
                                disabled={!hasChargers}
                                data-testid="activate-LB-button"
                            />
                        )}

                        <DropdownMenu data-testid="menu-delete-lbGroup">
                            <DropdownMenuItem
                                text={text('btnDeleteLbgroup')}
                                onClick={() => {
                                    confirmDialog({
                                        message: text('lblLoadBalancerDelete'),
                                        header: text('lblLoadBalancerDeleteHeading'),
                                        accept: () => {
                                            if (!loadBalancerGroup?.id) return

                                            removeLBGroup(loadBalancerGroup?.id)
                                        },
                                    })
                                }}
                                icon={<TrashIcon size={14} />}
                                key="btnDeleteLB group"
                                disabled={loadBalancerGroup?.active}
                                data-testid="btn-reset-charger"
                                disabledReason={text('msgLbActive')}
                            />
                        </DropdownMenu>
                    </>
                )}
            </div>
        </>
    )
}

export default LoadBalancerTypeLabel
